import { getSiteConfig } from '@shared/config';
import type { AddressFormData, AddressLookupResult } from '../types';

/**
 * Converts a form/UI phone prefix (`GB+44`, `44`, `+49`) to API dial code (`+44`, `+49`).
 */
function parseFormPrefixToDialCode(prefix: string | number): string {
  const prefixStr = String(prefix).replace(/\s+/g, '');
  const dialDigits = prefixStr.includes('+') ? (prefixStr.split('+').pop() ?? '') : prefixStr;

  return `+${dialDigits}`;
}

/**
 * Default international dial code from the active site's registration settings (`NEXT_PUBLIC_APP_CONFIG`).
 */
function getRegistrationInternationalDialCode(): string {
  return parseFormPrefixToDialCode(getSiteConfig().REGISTRATION.defaultPhonePrefix);
}

/**
 * Converts form prefix to API dial code; uses the market default when prefix is missing or blank.
 */
function toApiDialCode(prefix?: string | number): string {
  if (prefix === undefined || prefix === null || String(prefix).trim() === '') {
    return getRegistrationInternationalDialCode();
  }

  return parseFormPrefixToDialCode(prefix);
}

/**
 * Formats date of birth from form structure to API format (yyyyMMdd)
 */
function formatBirthdate(dateOfBirth?: { day?: number; month?: number; year?: number }): string {
  if (!dateOfBirth?.day || !dateOfBirth?.month || !dateOfBirth?.year) {
    return '';
  }

  const { day, month, year } = dateOfBirth;
  return `${year.toString().padStart(4, '0')}${month.toString().padStart(2, '0')}${day.toString().padStart(2, '0')}`;
}

/**
 * Maps period format from form to API expected values
 */
function mapPeriodToApiFormat(period: string | { label: string; value: number }): 'DAY' | 'WEEK' | 'MONTH' {
  const normalizedPeriod = typeof period === 'string' ? period.toLowerCase() : period?.label?.toLowerCase();

  switch (normalizedPeriod) {
    case 'daily':
    case 'day':
      return 'DAY';
    case 'weekly':
    case 'week':
      return 'WEEK';
    case 'monthly':
    case 'month':
      return 'MONTH';
    default:
      return 'DAY';
  }
}

/**
 * Parses street address to extract street number and name
 */
function parseStreetAddress(addressLine: string): { streetNr?: string; streetName: string } {
  if (!addressLine || typeof addressLine !== 'string') {
    return { streetName: String(addressLine || '') };
  }

  // Match leading spaces, then one or more digits optionally followed by letters, then whitespace
  const streetNumberMatch = addressLine.match(/^\s*(\d+[A-Za-z]*)\s+/);
  if (streetNumberMatch) {
    const streetNr = streetNumberMatch[1];
    const streetName = addressLine.replace(/^\s*\d+[A-Za-z]*\s+/, '').trim();
    return { streetNr, streetName };
  }
  // If no number found, treat whole line as street name
  return { streetName: addressLine.trim() };
}

/**
 * Extracts the street number from address data
 * Uses houseNameNumber (from form input) first, then buildingNumber, then buildingName
 */
function extractStreetNumber(data: AddressLookupResult | AddressFormData): string | undefined {
  // Handle both FormValues format (with address property) and direct address object format
  const address = 'address' in data ? data.address : data;

  // For form data, use houseNameNumber first (this is what gets set by the AddressStep)
  if ('houseNameNumber' in address && address.houseNameNumber && (address.houseNameNumber as string).trim() !== '') {
    return (address.houseNameNumber as string).trim();
  }

  // Prefer buildingNumber (which contains SubBuilding from Loqate API) first
  if (address?.buildingNumber && (address.buildingNumber as string).trim() !== '') {
    return (address.buildingNumber as string).trim();
  }
  // Then buildingName if buildingNumber is not available
  if (address?.buildingName && (address.buildingName as string).trim() !== '') {
    return (address.buildingName as string).trim();
  }

  // Fallback to parsing line1 if no building fields are available
  const line1 = 'line1' in address ? (address.line1 as string) || '' : '';
  const { streetNr: parsedStreetNr } = parseStreetAddress(line1);

  return parsedStreetNr;
}

/**
 * Processes address fields to extract streetName and streetNr for API payload
 */
function processAddressFields(address: AddressLookupResult | AddressFormData): { streetName: string | null; streetNr?: string } {
  // Handle undefined or null address
  if (!address) {
    return { streetName: null };
  }

  // Handle both FormValues format (with address property) and direct address object format
  const addressData = 'address' in address ? address.address : address;

  // For form data, use the streetName field directly (set by AddressStep handleAddressSelect)
  // For API lookup results, use the street field
  let streetName: string | null = null;

  if ('streetName' in addressData && addressData.streetName) {
    // Form data format - streetName was set by handleAddressSelect
    streetName = (addressData.streetName as string).trim() || null;
  } else if ('street' in addressData && addressData.street) {
    // API lookup format - use street field
    streetName = (addressData.street as string).trim() || null;
  } else if ('line1' in addressData) {
    // Fallback: parse line1 to extract street name when no structured fields are available
    const { streetName: parsedStreetName } = parseStreetAddress((addressData.line1 as string) || '');
    streetName = parsedStreetName || null;
  }

  // Extract street number using the proper logic
  const streetNr = extractStreetNumber(address);

  return {
    streetName,
    ...(streetNr ? { streetNr } : {}),
  };
}

/**
 * Creates base payload structure with common defaults
 */
function createBasePayload(overrides: Partial<Record<string, unknown>> = {}): Record<string, unknown> {
  const market = getSiteConfig().REGISTRATION;

  return {
    salutation: 'MR',
    language: market.language,
    country: market.country,
    ...overrides,
  };
}

/**
 * Removes `state` from player API payloads when the market does not submit the state select.
 */
function omitStateField(payload: Record<string, unknown>): Record<string, unknown> {
  if (getSiteConfig().REGISTRATION.showsStateSelect) {
    return payload;
  }

  const payloadWithoutState = { ...payload };
  delete payloadWithoutState.state;
  return payloadWithoutState;
}

/**
 * Transforms complete form data into API payload format for registration/validation
 */
function transformFormDataToApiPayload(formData: Record<string, unknown>): Record<string, unknown> {
  const market = getSiteConfig().REGISTRATION;
  const createAccount = formData.createAccount as Record<string, unknown>;
  const personalDetails = formData.personalDetails as Record<string, unknown>;
  const address = formData.address as Record<string, unknown>;
  const depositLimits = formData.depositLimits as Record<string, unknown>;
  const phoneNumber = formData.mobileNumber as Record<string, unknown>;
  const dateOfBirth = personalDetails?.dateOfBirth as Record<string, unknown>;
  const productPreferences = Array.isArray(formData.productPreferences) ? (formData.productPreferences as string[]) : [];
  const channelPreferences = Array.isArray(formData.channelPreferences) ? (formData.channelPreferences as string[]) : [];
  const optedIntoMarketingViaPrivacy = market.marketingConsentFromPrivacyPolicy && createAccount?.privacyPolicy === true;

  // Format birthdate as yyyyMMdd
  const birthdate = formatBirthdate(dateOfBirth as { day?: number; month?: number; year?: number });

  // Extract phone number components (remove whitespace if present)
  const mobilePhoneNumber = phoneNumber?.number ? (phoneNumber.number as string).replace(/\s+/g, '') : (phoneNumber?.number as string);
  const countryCode = toApiDialCode(phoneNumber?.prefix as string | undefined);

  // Process deposit limits with noLimit flag
  const depositAmount = depositLimits?.amount;
  const isNoLimit = depositAmount !== undefined && depositAmount !== null && parseInt(depositAmount.toString()) === -1;

  let limitAmount: number | string | undefined;
  let limitPeriod: 'DAY' | 'WEEK' | 'MONTH' | undefined;
  let noLimit = false;

  if (isNoLimit) {
    // No limit set ("Do it later" was selected)
    noLimit = true;
    limitAmount = '-100';
    limitPeriod = depositLimits?.period ? mapPeriodToApiFormat(depositLimits.period as string) : 'MONTH';
  } else if (depositAmount !== undefined && depositAmount !== null) {
    // Limit is set with a valid amount
    noLimit = false;
    limitAmount = parseInt(depositAmount.toString()) * 100;
    limitPeriod = depositLimits?.period ? mapPeriodToApiFormat(depositLimits.period as string) : 'MONTH';
  } else {
    // No deposit limits provided at all (shouldn't happen in normal flow)
    noLimit = false;
    limitAmount = undefined;
    limitPeriod = undefined;
  }

  const payload: Record<string, unknown> = {
    // Required fields
    email: createAccount?.email || '',
    salutation: (personalDetails?.salutation as 'MR' | 'MS' | 'MRS') || 'MR',
    firstname: personalDetails?.firstName || '',
    lastname: personalDetails?.lastName || '',
    password: (createAccount?.password as string) || 'HARDCODEDTESTPASSWORd123!',
    birthdate,
    language: market.language,
    country: market.country,

    // Address fields (flattened to root level)
    ...(address?.postcode ? { postCode: address.postcode as string } : {}),
    ...(address?.city ? { city: address.city as string } : {}),
    ...(market.showsStateSelect && address?.state ? { state: address.state as string } : {}),
    ...processAddressFields(address),
    ...(address?.line2 ? { extraAddressField: address.line2 as string } : {}),

    // Optional fields
    mobilePhoneNumber,
    countryCode,
    gdprChecked: createAccount?.gdprChecked ?? createAccount?.termsAndConditions ?? createAccount?.privacyPolicy ?? createAccount?.privacyNotice,

    // Deposit limits with noLimit flag
    noLimit,
    ...(limitAmount !== undefined ? { limitAmount } : {}),
    ...(limitPeriod ? { limitPeriod } : {}),

    // Include Product and Channel Preferences as false even if they are empty
    registrationContactPreferencesData: {
      notificationPreferences: {
        lottery: productPreferences.includes('lottery'),
        casinoAndScratchcards: productPreferences.includes('casino'),
        sportsbook: productPreferences.includes('sportsbook'),
        bingo: false,
        email: optedIntoMarketingViaPrivacy || channelPreferences.includes('email'),
        sms: optedIntoMarketingViaPrivacy || channelPreferences.includes('sms'),
        phone: optedIntoMarketingViaPrivacy || channelPreferences.includes('phone'),
        post: false,
      },
    },
  };

  return payload;
}

export {
  createBasePayload,
  extractStreetNumber,
  formatBirthdate,
  getRegistrationInternationalDialCode,
  mapPeriodToApiFormat,
  omitStateField,
  parseFormPrefixToDialCode,
  parseStreetAddress,
  processAddressFields,
  toApiDialCode,
  transformFormDataToApiPayload,
};
